/**
 * Modal pour afficher le QR Code WhatsApp
 */
import { useEffect } from 'react'

interface WhatsAppQRModalProps {
  isOpen: boolean
  qrCode: string | null
  onClose: () => void
}

export default function WhatsAppQRModal({ isOpen, qrCode, onClose }: WhatsAppQRModalProps) {
  // Fermer avec Escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    
    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      return () => document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen, onClose])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="relative w-full max-w-2xl mx-4 bg-white dark:bg-zinc-900 rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-zinc-700">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">
            Lier un compte WhatsApp
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-zinc-800 transition"
          >
            <svg className="w-6 h-6 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="grid md:grid-cols-2 gap-6">
            {/* QR Code */}
            <div className="flex items-center justify-center bg-white p-6 rounded-xl border-2 border-gray-200 dark:border-zinc-700">
              {qrCode ? (
                <img src={qrCode} alt="QR Code WhatsApp" className="w-full max-w-xs" />
              ) : (
                <div className="w-64 h-64 flex items-center justify-center text-gray-400">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500"></div>
                </div>
              )}
            </div>

            {/* Instructions */}
            <div className="flex flex-col justify-center space-y-4">
              <div className="flex items-start gap-3 text-green-600 dark:text-green-400">
                <svg className="w-6 h-6 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                    Comment scanner le code QR ?
                  </h3>
                </div>
              </div>

              <ol className="space-y-3 text-sm text-gray-600 dark:text-gray-400">
                <li className="flex items-start gap-2">
                  <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded-full text-xs font-bold">
                    1
                  </span>
                  <span>Ouvrez WhatsApp sur votre téléphone</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded-full text-xs font-bold">
                    2
                  </span>
                  <span>Appuyez sur Menu ou Paramètres et sélectionnez Appareils connectés</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded-full text-xs font-bold">
                    3
                  </span>
                  <span>Appuyez sur Connecter un appareil</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded-full text-xs font-bold">
                    4
                  </span>
                  <span>Pointez votre téléphone vers ce code QR pour le scanner</span>
                </li>
              </ol>

              <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-900/50 rounded-lg">
                <p className="text-xs text-yellow-800 dark:text-yellow-200">
                  ⏱️ Ce QR code expire après quelques minutes. Si la connexion échoue, fermez cette fenêtre et réessayez.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 dark:border-zinc-700 bg-gray-50 dark:bg-zinc-800/50">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-gray-200 hover:bg-gray-300 dark:bg-zinc-700 dark:hover:bg-zinc-600 text-gray-900 dark:text-white font-medium rounded-lg transition"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  )
}
